import React, { FC, useState, ReactNode, useMemo, MouseEvent } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { useSelector } from 'react-redux'

import Box from '@mui/material/Box'

import Cell from './Cell'
import Row from './Row'
import Root from './Root'
import LayoutWrapper from './LayoutWrapper'
import RenderCell from './RenderCell'
import Toolbar from './Toolbar'
import Sidebar from './Sidebar'
import Commands from './Commands'
import EditorContainer from './EditorContainer'
import RenderContext from '@kaidam/fc-shared/src/layout/RenderContext'
import EditorContext, {
  getDefaultContext,
} from './EditorContext'

import { Editor, Frame, Element } from '@craftjs/core'

import {
  IVoidCallback,
} from '@kaidam/fc-shared/src/types/utils'

import {
  ILayout,
  ILayout_Render_Context,
  ILayout_Render_Context_With_Options,
  ILayout_Options,
  ILayout_EditorEvent,
  ILayout_ChangeEvent,
} from '@kaidam/fc-shared/src/types/layout'

import {
  LAYOUT_OPTIONS_WIDTH_DEFAULT,
} from '@kaidam/fc-shared/src/constants'

import {
  extractSetValue,
} from './utils'

import routerActions from '../../store/modules/router'
import routerSelectors from '../../store/selectors/router'
import { autocompleteClasses } from '@mui/material'

interface LayoutEditorProps {
  data: ILayout,
  context: ILayout_Render_Context,
  options: ILayout_Options,
  toolbarButtons?: ReactNode,
  toolbarButtonsBefore?: ReactNode,
  onChange: {
    (data: ILayout): void,
  },
}

const useStyles = makeStyles(theme => ({
  '@global': {
    '.Layout-Editor-Margin': {
      mr: 1,
    },
    '.Layout-Editor-Row': {
      '&.active, &.hover': {
        '& .Row-Inner': {
          backgroundColor: '#999999',
        },
        '& .Layout-Editor-Cell': {
          border: `1px dotted ${theme.palette.primary.main}`,
          boxShadow: '3px 3px 5px 0px rgba(0,0,0,0.25)',
        },
      },
      '& > .Layout-Editor-Row-Drag > .Layout-Editor-Row-Drag-Inner': {
        backgroundColor: '#f5f5f5',
        border: '1px solid #ccc',
      },
      '&.hover > .Layout-Editor-Row-Drag > .Layout-Editor-Row-Drag-Inner': {
        backgroundColor: '#f5f5f5',
        border: `1px solid ${theme.palette.primary.main}`,
      },
      '&.active > .Layout-Editor-Row-Drag > .Layout-Editor-Row-Drag-Inner': {
        backgroundColor: '#999999',
        border: `1px solid ${theme.palette.primary.main}`,
      },
    },
    '.Layout-Editor-Cell': {
      border: `1px dotted #ccc`,
      boxShadow: 'none',
      '&.active, &.hover': {
        border: `1px dotted ${theme.palette.primary.main}`,
        boxShadow: '3px 3px 5px 0px rgba(0,0,0,0.25)',
      },
      '& .Cell-Inner': {
        border: '1px dashed transparent',
      },
      '&.active .Cell-Inner': {
        border: `1px dashed ${theme.palette.primary.main}`,
      },
    },
  }
}))

const LayoutEditor: FC<React.PropsWithChildren<LayoutEditorProps>> = ({
  data,
  context,
  options,
  toolbarButtons,
  toolbarButtonsBefore,
  onChange,
}) => {
  const classes = useStyles()

  const [ changeEvent, setChangeEvent ] = useState<ILayout_ChangeEvent | null>(null)
  const [ editorEvent, setEditorEvent ] = useState<ILayout_EditorEvent | null>(null)
  const [ selectionLocked, setSelectionLocked ] = useState(false)

  const useContext = useMemo<ILayout_Render_Context_With_Options>(() => {
    return Object.assign({}, context, {options})
  }, [
    context,
    options,
  ])

  // this is just to get around the ts compiler
  const RootAny = Root as any
  const RowAny = Row as any
  const CellAny = Cell as any

  const route = useSelector(routerSelectors.route)

  return (
    <RenderContext.Provider value={ useContext }>
      <EditorContext.Provider value={ getDefaultContext({
        options,
        changeEvent,
        setChangeEvent,
        editorEvent,
        setEditorEvent,
        selectionLocked,
        setSelectionLocked,
      }) }>
        
        <Editor
          resolver={{ RootAny, RowAny, CellAny }}
          onRender={ RenderCell }
          onNodesChange={(query) => {
            const ev: ILayout_ChangeEvent = {}
            // trigger the layout wrapper
            // which has the full useEditor api
            // (because it's inside the editor)
            const draggedId = extractSetValue(query.getState().events.dragged)
            const placement = query.getState().indicator?.placement
            if(placement && placement.parent) {
              if(draggedId) ev.dragId = draggedId
              ev.dropId = placement.parent.id
              ev.dropIndex = placement.index + (
                placement.where == 'before' ?
                  0 :
                  1
              )
              setChangeEvent(ev)
            }
          }}
        >
          <Commands />
          <LayoutWrapper>
            <EditorContainer>
              <Box
                className="container"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                  height: '100%',
                }}
              >
                <Box
                  className="content"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    height: '100%',
                    mr: 2,
                  }}
                >
                  <Box
                    className="toolbar"
                    sx={{
                      height: '70px',
                      minHeight: '70px',
                      flexGrow: 0,
                    }}
                  >
                    <Toolbar
                      buttons={ toolbarButtons }
                      buttonsBefore={ toolbarButtonsBefore }
                      options={ options }
                      onChange={ onChange }
                    />
                  </Box>
                  <Box
                    className="layout"
                    sx={{
                      flexGrow: 1,
                      border: '1px solid #ccc',
                      overflowY: 'auto',
                    }}
                  >
                    <Frame>
                      <Element
                        canvas
                        is={ Root }
                      >
                        {
                          data.rows.map((row, i) => {
                            return (
                              <RowAny
                                key={ i }
                                row={ row }
                                config={ row.config }
                                options={ options }
                              />
                            )
                          })
                        }
                      </Element>
                    </Frame>
                  </Box>
                </Box>
                <Box
                  className="content"
                  sx={{
                    width: '300px',
                    minWidth: '300px',
                    flexGrow: 0,
                    overflowY: 'auto',
                    height: '100%',
                  }}
                >
                  <Sidebar
                    onChange={ onChange }
                  />
                </Box>
              </Box>
            </EditorContainer>
          </LayoutWrapper>
        </Editor>
      </EditorContext.Provider>
    </RenderContext.Provider>
  )

}

export default LayoutEditor